// src/events/oraganiser_artist_invite_form.js
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query as fsQuery,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  FiSave,
  FiUser,
  FiAtSign,
  FiPhone,
  FiMessageCircle,
  FiMapPin,
  FiImage,
  FiUpload,
  FiCheckCircle,
  FiX,
  FiHelpCircle,
} from "react-icons/fi";

/* ──────────────────────────────────────────────────────────
   THEME  (mobile-first, app-like)
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e5e7eb",
  page: "#f7f7fb",
  card: "#ffffff",
  successBg: "#ecfdf5",
  successText: "#065f46",
  warnBg: "#fff7ed",
  warnText: "#9a3412",
};

const inputStyle = {
  width: "100%",
  height: 48,
  padding: "0 14px",
  marginTop: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
  fontSize: 16,
  boxSizing: "border-box",
  WebkitTapHighlightColor: "transparent",
};

const labelStyle = {
  fontSize: 13,
  color: COLORS.subtext,
  display: "inline-grid",
  gridAutoFlow: "column",
  gap: 8,
  alignItems: "center",
};

const card = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 6px 18px rgba(9, 9, 11, 0.05)",
  boxSizing: "border-box",
};

const primaryBtn = {
  height: 52,
  padding: "0 18px",
  borderRadius: 14,
  border: "none",
  color: "#fff",
  fontWeight: 900,
  letterSpacing: 0.2,
  fontSize: 16,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const subtleBtn = {
  border: "none",
  background: "transparent",
  color: COLORS.subtext,
  height: 44,
  padding: "0 10px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
  fontSize: 14,
};

/* ──────────────────────────────────────────────────────────
   UTIL
   ────────────────────────────────────────────────────────── */
const digitsOnly = (s = "") => String(s).replace(/\D/g, "");

/* ──────────────────────────────────────────────────────────
   PAGE (mobile app style)
   ────────────────────────────────────────────────────────── */
export default function OrganiserArtistInviteForm() {
  const [form, setForm] = useState({
    fullName: "",
    stageName: "",
    email: "",
    phone: "",
    whatsapp: "",
    location: "",
    notes: "",
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const phoneClean = useMemo(() => digitsOnly(form.phone), [form.phone]);
  const whatsappClean = useMemo(() => digitsOnly(form.whatsapp), [form.whatsapp]);

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function onPickFile() {
    fileInputRef.current?.click();
  }

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//i.test(f.type)) {
      setError("Please select an image file.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("Image must be ≤ 5 MB.");
      return;
    }
    setError("");
    setPhotoFile(f);
    const url = URL.createObjectURL(f);
    setPhotoPreview(url);
  }

  // Clean preview URL
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  // 🔐 Duplicate guard (by phone and email)
  async function checkDuplicate(invitesColRef, phone, rawEmail) {
    // Check phone first
    if (phone) {
      const snapPhone = await getDocs(
        fsQuery(invitesColRef, where("phone", "==", phone), limit(1))
      );
      if (!snapPhone.empty) {
        return "This phone number has already been submitted. If you need to update details, please message us on WhatsApp.";
      }
    }

    // Check email if provided (compare both raw and lowercased to be safe)
    const emailTrim = (rawEmail || "").trim();
    if (emailTrim) {
      const emailLower = emailTrim.toLowerCase();

      // 1) exact as typed
      const snapEmailExact = await getDocs(
        fsQuery(invitesColRef, where("email", "==", emailTrim), limit(1))
      );
      if (!snapEmailExact.empty) {
        return "This email has already been submitted.";
      }

      // 2) lowercased
      if (emailLower !== emailTrim) {
        const snapEmailLower = await getDocs(
          fsQuery(invitesColRef, where("email", "==", emailLower), limit(1))
        );
        if (!snapEmailLower.empty) {
          return "This email has already been submitted.";
        }
      }
    }

    return ""; // no duplicates found
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.fullName.trim()) return setError("Full name is required.");
    if (!phoneClean) return setError("Phone number is required.");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return setError("Please enter a valid email address.");
    }

    setSaving(true);

    try {
      const invitesColRef = collection(db, "oraganiser_artist_invite_form");

      // ✅ Check duplicates before any upload/write
      const dupMsg = await checkDuplicate(invitesColRef, phoneClean, form.email);
      if (dupMsg) {
        setSaving(false);
        setError(dupMsg);
        return;
      }

      // Upload image if provided
      let uploadedUrl = null;
      let storagePath = null;

      if (photoFile) {
        const storage = getStorage();
        const safeName = photoFile.name.replace(/[^\w.-]+/g, "_");
        storagePath = `artist_invites/${Date.now()}-${safeName}`;
        const ref = sRef(storage, storagePath);
        await uploadBytes(ref, photoFile, { contentType: photoFile.type });
        uploadedUrl = await getDownloadURL(ref);
      }

      // Normalize email to lowercase for consistency
      const emailNormalized = form.email ? form.email.trim().toLowerCase() : null;

      const payload = {
        fullName: form.fullName.trim(),
        stageName: form.stageName.trim() || null,
        email: emailNormalized,
        phone: phoneClean,
        whatsapp: whatsappClean || null,
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
        photoURL: uploadedUrl,
        storagePath,
        status: "pending", // ✅ always save as pending
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: "public_invite_form",
      };

      await addDoc(invitesColRef, payload);

      setSaving(false);
      setModalOpen(true);

      // Reset form
      setForm({
        fullName: "",
        stageName: "",
        email: "",
        phone: "",
        whatsapp: "",
        location: "",
        notes: "",
      });
      setPhotoFile(null);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview("");
    } catch (err) {
      console.error(err);
      setSaving(false);
      setError("Could not submit. Please try again.");
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
      }}
    >
      {/* App Bar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
          color: "#fff",
          borderBottom: "1px solid rgba(255,255,255,.2)",
          paddingTop: "max(10px, env(safe-area-inset-top))",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            margin: "0 auto",
            padding: "10px 14px",
            display: "grid",
            gridTemplateColumns: "52px 1fr 52px",
            alignItems: "center",
            boxSizing: "border-box",
          }}
        >
          <div />

          <div
            style={{
              textAlign: "center",
              fontWeight: 900,
              fontSize: 18,
              letterSpacing: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Artist Invite Form
          </div>

          <div />
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          maxWidth: 560,
          width: "100%",
          margin: "0 auto",
          padding: "14px 14px 100px",
          boxSizing: "border-box",
        }}
      >
        {/* Tip card */}
        <div
          style={{
            ...card,
            padding: 12,
            marginBottom: 12,
            background:
              "linear-gradient(90deg, rgba(242,12,83,.06), rgba(251,108,51,.06))",
            borderStyle: "dashed",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: COLORS.subtext }}>
            <FiHelpCircle />
            This public form is for artists invited by an organiser. Please share accurate information.
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} style={card}>
          <div style={{ display: "grid", gap: 14 }}>
            <label>
              <span style={labelStyle}>
                <FiUser /> Full name <span style={{ color: COLORS.primary }}>*</span>
              </span>
              <input
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
                placeholder="e.g., Priya Sharma"
                style={inputStyle}
                autoComplete="name"
              />
            </label>

            <label>
              <span style={labelStyle}>
                <FiUser /> Stage name (optional)
              </span>
              <input
                value={form.stageName}
                onChange={(e) => setField("stageName", e.target.value)}
                placeholder="e.g., DJ Prism"
                style={inputStyle}
                autoComplete="nickname"
              />
            </label>

            <label>
              <span style={labelStyle}>
                <FiAtSign /> Email
              </span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="name@example.com"
                style={inputStyle}
                autoComplete="email"
                inputMode="email"
              />
            </label>

            <label>
              <span style={labelStyle}>
                <FiPhone /> Phone <span style={{ color: COLORS.primary }}>*</span>
              </span>
              <input
                value={form.phone}
                onChange={(e) => setField("phone", digitsOnly(e.target.value))}
                placeholder="10-digit number"
                style={inputStyle}
                inputMode="tel"
                autoComplete="tel"
                maxLength={15}
              />
            </label>

            <label>
              <span style={labelStyle}>
                <FiMessageCircle /> WhatsApp
              </span>
              <input
                value={form.whatsapp}
                onChange={(e) => setField("whatsapp", digitsOnly(e.target.value))}
                placeholder="10-digit WhatsApp number"
                style={inputStyle}
                inputMode="tel"
                autoComplete="tel-national"
                maxLength={15}
              />
            </label>

            <label>
              <span style={labelStyle}>
                <FiMapPin /> Location
              </span>
              <input
                value={form.location}
                onChange={(e) => setField("location", e.target.value)}
                placeholder="City, Country"
                style={inputStyle}
                autoComplete="address-level2"
              />
            </label>

            {/* Photo upload */}
            <div>
              <span style={labelStyle}>
                <FiImage /> Artist photo
              </span>

              <div
                role="button"
                onClick={onPickFile}
                style={{
                  marginTop: 8,
                  border: `2px dashed ${COLORS.border}`,
                  background: "#fff",
                  borderRadius: 16,
                  padding: 12,
                  display: "grid",
                  gridTemplateColumns: "96px 1fr",
                  gap: 12,
                  alignItems: "center",
                  cursor: "pointer",
                }}
                title="Tap to upload image"
              >
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 12,
                    border: `1px solid ${COLORS.border}`,
                    background: photoPreview
                      ? `url(${photoPreview}) center/cover no-repeat`
                      : "#f3f4f6",
                    display: "grid",
                    placeItems: "center",
                    overflow: "hidden",
                  }}
                >
                  {!photoPreview && <FiImage size={24} color={COLORS.subtext} />}
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      color: COLORS.text,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                    }}
                  >
                    <FiUpload />
                    Tap to choose an image (max 5&nbsp;MB)
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.subtext }}>
                    Accepted: JPG, PNG, WEBP.
                  </div>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
            </div>

            <label>
              <span style={labelStyle}>Notes</span>
              <textarea
                rows={5}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Any additional info (genres, references, agent contact, etc.)"
                style={{
                  ...inputStyle,
                  height: 120,
                  paddingTop: 12,
                  lineHeight: 1.45,
                  resize: "vertical",
                }}
              />
            </label>

            {/* Error inline */}
            {error && (
              <div
                style={{
                  marginTop: 2,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: COLORS.warnBg,
                  color: COLORS.warnText,
                  border: "1px solid #fed7aa",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14,
                }}
              >
                <FiHelpCircle />
                {error}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Sticky Footer Action Bar */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 9,
          background: "#fff",
          borderTop: `1px solid ${COLORS.border}`,
          paddingBottom: "max(10px, env(safe-area-inset-bottom))",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            margin: "0 auto",
            padding: "10px 14px",
            display: "grid",
            gap: 8,
            boxSizing: "border-box",
          }}
        >
          <button
            type="button"
            style={subtleBtn}
            onClick={() => {
              setForm({
                fullName: "",
                stageName: "",
                email: "",
                phone: "",
                whatsapp: "",
                location: "",
                notes: "",
              });
              if (photoPreview) URL.revokeObjectURL(photoPreview);
              setPhotoPreview("");
              setPhotoFile(null);
              setError("");
            }}
            aria-label="Reset form"
          >
            Reset form
          </button>

          <button
            onClick={(e) => onSubmit(e)}
            disabled={saving}
            style={primaryBtn}
            aria-label="Submit invite"
          >
            <FiSave />
            {saving ? "Submitting…" : "Submit invite"}
          </button>
        </div>
      </div>

      {/* In-page Modal (sheet-style) */}
      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Submission successful"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            display: "grid",
            placeItems: "end center",
            padding: 16,
            zIndex: 50,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              background: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 -18px 40px rgba(0,0,0,.25)",
              overflow: "hidden",
              transform: "translateY(0)",
            }}
          >
            {/* Modal header gradient */}
            <div
              style={{
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
                color: "#fff",
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 900 }}>
                <FiCheckCircle size={22} />
                Submission received
              </div>
              <button
                onClick={() => setModalOpen(false)}
                title="Close"
                style={{
                  border: "none",
                  background: "rgba(255,255,255,.15)",
                  color: "#fff",
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                }}
              >
                <FiX />
              </button>
            </div>

            <div style={{ padding: 16, display: "grid", gap: 12 }}>
              <div
                style={{
                  background: COLORS.successBg,
                  color: COLORS.successText,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid #a7f3d0",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontWeight: 700,
                }}
              >
                <FiCheckCircle />
                Thank you! Your artist details have been submitted.
              </div>

              <div style={{ color: COLORS.text, fontSize: 14, lineHeight: 1.55 }}>
                We’ll review and get back to you soon. If you have any concerns, reach us on
                WhatsApp at{" "}
                <a
                  href="https://wa.me/918099975322"
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontWeight: 900, color: COLORS.primary, textDecoration: "none" }}
                >
                  +91&nbsp;80999&nbsp;75322
                </a>{" "}
                — Team ApnaArtist.
              </div>

              <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
                <a
                  href="https://wa.me/918099975322"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    ...primaryBtn,
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiMessageCircle />
                  WhatsApp Us
                </a>
                <button
                  onClick={() => setModalOpen(false)}
                  style={subtleBtn}
                  title="Close and stay"
                >
                  Close
                </button>
              </div>

              <div style={{ height: 8 }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
